import { Application, Graphics, type Ticker } from 'pixi.js';
import type { Map as MlMap } from 'maplibre-gl';
import type { KingdomCollection } from '../api/models';

interface Marker {
  lng: number;
  lat: number;
  gfx: Graphics;
  phase: number;
}

// KingdomOverlay is a transparent PixiJS canvas over the MapLibre map — the web
// analog of the mobile Flame projection bridge (§2). This slice only pulses a
// marker at each kingdom's label anchor to prove the two-layer architecture;
// animated game entities attach to the same reprojection path later.
export class KingdomOverlay {
  private readonly app = new Application();
  private markers: Marker[] = [];
  private map?: MlMap;

  async mount(map: MlMap, container: HTMLElement): Promise<void> {
    this.map = map;
    await this.app.init({ resizeTo: container, backgroundAlpha: 0, antialias: true });

    const canvas = this.app.canvas;
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.pointerEvents = 'none';
    container.appendChild(canvas);

    // Reproject on every map render so markers stay pinned during pan/zoom.
    map.on('render', () => this.reproject());
    this.app.ticker.add((ticker: Ticker) => this.animate(ticker.deltaTime));
  }

  setKingdoms(data: KingdomCollection): void {
    for (const m of this.markers) m.gfx.destroy();
    this.markers = [];

    for (const f of data.features) {
      const { label_lng: lng, label_lat: lat } = f.properties;
      if (lng === undefined || lat === undefined) continue;

      const gfx = new Graphics();
      gfx.circle(0, 0, 6).fill({ color: 0xc9a227, alpha: 0.9 });
      this.app.stage.addChild(gfx);
      this.markers.push({ lng, lat, gfx, phase: 0 });
    }
    this.reproject();
  }

  private reproject(): void {
    if (!this.map) return;
    for (const m of this.markers) {
      const p = this.map.project([m.lng, m.lat]);
      m.gfx.position.set(p.x, p.y);
    }
  }

  private animate(delta: number): void {
    for (const m of this.markers) {
      m.phase += 0.05 * delta;
      m.gfx.scale.set(1 + Math.sin(m.phase) * 0.2);
    }
  }
}
